import { lazy, Suspense, useState, useEffect, useCallback, useMemo } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import { Toaster } from 'sonner';
import { isFeatureEnabled } from '@/config/rollout';
import PageLoadBoundary, { QuietErrorBoundary } from '@/components/PageLoadBoundary';
import { ScientistViewContext, ScientistViewBanner, scientistViewUser, confirmViewChange } from '@/lib/ScientistViewContext';
import { hostedLoginReturnPath } from '@/features/hosted-mcp/connectionFlow';
import { consumeMicrosoftLoginBinding } from '@/lib/microsoftLogin';

// Import pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
const ProtocolsPage = lazy(() => import('./pages/ProtocolsPage'));
const BuffersPage = lazy(() => import('./pages/BuffersPage'));
const ProtocolBuilderPage = lazy(() => import('./pages/ProtocolBuilderPage'));
const InventoryPage = lazy(() => import('./pages/InventoryPage'));
const CellMaintenancePage = lazy(() => import('./pages/CellMaintenancePage'));
const MouseRecordsPage = lazy(() => import('./pages/MouseRecordsPage'));
const ProcurementPage = lazy(() => import('./pages/ProcurementPage'));
const ProjectsPage = lazy(() => import('./pages/ProjectsPage'));
const ExperimentsPage = lazy(() => import('./pages/ExperimentsPage'));
const FeatureAccessPage = lazy(() => import('./pages/FeatureAccessPage'));
const LegacyWorkspaceRoute = lazy(() => import('./pages/ReleaseTestingPage').then(module => ({ default: module.LegacyWorkspaceRoute })));
const DataAnalysisPage = lazy(() => import('./pages/DataAnalysisPage'));
const FinancialsPage = lazy(() => import('./pages/FinancialsPage'));
const ApprovalCenterPage = lazy(() => import('./pages/ApprovalCenterPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const HostedConnectionPage = lazy(() => import('./pages/HostedConnectionPage'));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ENABLE_DEMO_LOGIN = process.env.REACT_APP_ENABLE_DEMO_LOGIN === 'true';

const phaseRoute = (user, feature, element) => {
  if (!user) return <Navigate to="/login" />;
  return isFeatureEnabled(feature) ? element : <Navigate to="/" />;
};

// Auto-login component for testing purposes
const DEMO_LOGIN_ENDPOINTS = {
  admin: '/auth/demo-admin',
  manager: '/auth/demo-manager',
  user: '/auth/demo-scientist',
};

const DEMO_LOGIN_LABELS = {
  admin: 'Demo Admin',
  manager: 'Demo Manager',
  user: 'Demo Scientist',
};

function AutoLogin({ role, onLogin }) {
  const navigate = useNavigate();

  useEffect(() => {
    const performAutoLogin = async () => {
      try {
        if (!ENABLE_DEMO_LOGIN) {
          navigate('/login');
          return;
        }
        const endpoint = DEMO_LOGIN_ENDPOINTS[role];
        if (!endpoint) {
          navigate('/login');
          return;
        }
        const res = await axios.post(`${API}${endpoint}`);
        onLogin(res.data.token, res.data.user);
        navigate('/');
      } catch (err) {
        console.error('Auto-login failed:', err);
        navigate('/login');
      }
    };
    performAutoLogin();
  }, [role, onLogin, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-lg font-medium">Logging in as {DEMO_LOGIN_LABELS[role] || 'Demo User'}...</div>
    </div>
  );
}

// Axios interceptor for auth
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axios.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.dispatchEvent(new Event('auth-expired'));
    }
    return Promise.reject(error);
  }
);

// Sliding session renewal: while the app is open, exchange the token for a
// fresh one well before it expires so active users are never kicked mid-work.
// Idle sessions still expire — an expired token cannot be refreshed.
const SESSION_REFRESH_INTERVAL_MS = 10 * 60 * 1000;
const SESSION_REFRESH_THRESHOLD_MS = 2 * 60 * 60 * 1000;

function tokenExpiryMs(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

async function refreshSessionToken() {
  const token = localStorage.getItem('token');
  if (!token) return;
  const exp = tokenExpiryMs(token);
  if (exp && exp - Date.now() > SESSION_REFRESH_THRESHOLD_MS) return;
  try {
    const res = await axios.post(`${API}/auth/refresh`);
    if (res.data?.access_token) localStorage.setItem('token', res.data.access_token);
  } catch {
    // A genuine 401 here is handled by the response interceptor (logout).
  }
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scientistView, setScientistView] = useState(false);
  const previewActive = scientistView && user?.role === 'admin';
  const viewUser = useMemo(() => scientistViewUser(user, previewActive), [user, previewActive]);
  const changeView = useCallback(active => {
    if (active && user?.role !== 'admin') return false;
    if (!confirmViewChange()) return false;
    setScientistView(active); return true;
  }, [user]);

  useEffect(() => {
    // The fragment is not sent to web servers or included in referrer headers.
    // Clear it before fetching the signed-in human profile.
    const params = new URLSearchParams(window.location.hash.slice(1));
    const msToken = params.get('token');

    if (msToken) {
      window.history.replaceState({}, '', window.location.pathname + window.location.search);
      if (!consumeMicrosoftLoginBinding(params.get('login_binding'))) {
        window.history.replaceState({}, '', '/login?error=microsoft_login_binding');
        setLoading(false);
        return;
      }
      localStorage.setItem('token', msToken);
      axios.get(`${API}/users/me`, { headers: { Authorization: `Bearer ${msToken}` } })
        .then(res => {
          setUser(res.data);
          setLoading(false);
        })
        .catch(() => {
          localStorage.removeItem('token');
          setLoading(false);
        });
      return;
    }

    // Existing session restore
    const token = localStorage.getItem('token');
    if (token) {
      axios.get(`${API}/users/me`)
        .then(res => {
          setUser(res.data);
          setLoading(false);
        })
        .catch(() => {
          localStorage.removeItem('token');
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const handleLogin = (token, userData) => {
    setScientistView(false);
    localStorage.setItem('token', token);
    setUser(userData);
  };

  const handleLogout = () => {
    setScientistView(false);
    localStorage.removeItem('token');
    setUser(null);
  };

  // Listen for auth-expired events from embedded apps (timeline planner, etc.)
  useEffect(() => {
    const onAuthExpired = () => {
      setScientistView(false);
      localStorage.removeItem('token');
      setUser(null);
    };
    window.addEventListener('auth-expired', onAuthExpired);
    return () => window.removeEventListener('auth-expired', onAuthExpired);
  }, []);

  // Keep the session alive while the user is active: refresh on an interval
  // and whenever the window regains focus.
  useEffect(() => {
    if (!user) return undefined;
    refreshSessionToken();
    const interval = setInterval(refreshSessionToken, SESSION_REFRESH_INTERVAL_MS);
    window.addEventListener('focus', refreshSessionToken);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', refreshSessionToken);
    };
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg font-medium">Loading...</div>
      </div>
    );
  }

  return (
    <>
      <BrowserRouter>
        <ScientistViewContext.Provider value={{ active: previewActive, actualUser: user, enter: () => changeView(true), exit: () => changeView(false) }}>
        <ScientistViewBanner />
        <PageLoadBoundary>
        <Suspense fallback={<div className="p-8" role="status">Opening page…</div>}>
        <Routes key={previewActive ? 'scientist-preview' : 'account-view'}>
          {/* Auto-login route for testing */}
          <Route
            path="/autologin/admin"
            element={<AutoLogin role="admin" onLogin={handleLogin} />}
          />
          <Route
            path="/autologin/manager"
            element={<AutoLogin role="manager" onLogin={handleLogin} />}
          />
          <Route
            path="/autologin/user"
            element={<AutoLogin role="user" onLogin={handleLogin} />}
          />
          <Route
            path="/login"
            element={!user ? <LoginPage onLogin={handleLogin} /> : <Navigate to={hostedLoginReturnPath()} replace />}
          />
          <Route
            path="/connect/hosted"
            element={<HostedConnectionPage user={viewUser} />}
          />
          <Route
            path="/"
            element={viewUser?.role === 'scientist' && isFeatureEnabled('timeline')
              ? <Navigate to="/projects" replace />
              : user ? <DashboardPage user={viewUser} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/dashboard"
            element={user ? <DashboardPage user={viewUser} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route 
            path="/admin" 
            element={
              viewUser && viewUser.role === 'admin'
                ? <AdminPage />
                : <Navigate to='/' />

            } 
          />
          <Route
            path="/protocols"
            element={phaseRoute(user, 'protocols', <ProtocolsPage user={viewUser} onLogout={handleLogout} />)}
          />
          <Route
            path="/protocols/new"
            element={phaseRoute(user, 'protocols', <ProtocolBuilderPage user={viewUser} onLogout={handleLogout} />)}
          />
          <Route
            path="/protocols/:id"
            element={phaseRoute(user, 'protocols', <ProtocolBuilderPage user={viewUser} onLogout={handleLogout} />)}
          />
          <Route
            path="/inventory"
            element={phaseRoute(user, 'inventory', <InventoryPage user={viewUser} onLogout={handleLogout} />)}
          />
          <Route
            path="/cell-maintenance"
            element={phaseRoute(user, 'cellMaintenance', <CellMaintenancePage user={viewUser} onLogout={handleLogout} />)}
          />
          <Route
            path="/mouse-records"
            element={phaseRoute(user, 'mouseRecords', <MouseRecordsPage user={viewUser} onLogout={handleLogout} />)}
          />
          <Route
            path="/procurement"
            element={phaseRoute(user, 'procurement', <ProcurementPage user={viewUser} onLogout={handleLogout} />)}
          />
          <Route
            path="/projects"
            element={phaseRoute(user, 'timeline', <ProjectsPage user={viewUser} onLogout={handleLogout} />)}
          />
          <Route
            path="/timeline-planner"
            element={phaseRoute(user, 'timeline', <Navigate to="/projects" />)}
          />
          <Route
            path="/experiments"
            element={phaseRoute(user, 'experimentTracking', <ExperimentsPage user={viewUser} onLogout={handleLogout} />)}
          />
          <Route path="/buffers" element={user ? <BuffersPage user={viewUser} onLogout={handleLogout} /> : <Navigate to="/login" />} />
          <Route path="/feature-access" element={phaseRoute(user, 'experimentTracking', <FeatureAccessPage user={viewUser} onLogout={handleLogout} />)} />
          <Route path="/release-testing" element={<Navigate to="/feature-access" replace />} />
          <Route path="/yeast-library" element={phaseRoute(user, 'experimentTracking', <LegacyWorkspaceRoute feature="yeast" user={viewUser} />)} />
          <Route path="/colony-tracking" element={phaseRoute(user, 'experimentTracking', <LegacyWorkspaceRoute feature="colony" user={viewUser} />)} />
          <Route path="/data-analysis" element={phaseRoute(user, 'experimentTracking', <DataAnalysisPage user={viewUser} onLogout={handleLogout} />)} />
          <Route
            path="/financials"
            element={phaseRoute(user, 'financials', <FinancialsPage user={viewUser} onLogout={handleLogout} />)}
          />
          <Route
            path="/approvals"
            element={phaseRoute(user, 'approvals', <ApprovalCenterPage user={viewUser} onLogout={handleLogout} />)}
          />
        </Routes>
        </Suspense>
        </PageLoadBoundary>
        </ScientistViewContext.Provider>
        <QuietErrorBoundary>
          <Toaster position="top-right" />
        </QuietErrorBoundary>
      </BrowserRouter>
    </>
  );
}

export default App;
